import api from './api'
import { extractErrorMessage } from '../utils/errorHandler'

export interface AnalyzeFileRequest {
  upload_id: number
  data_source_id: number
  category: string
}

export interface SuggestedMapping {
  original_header: string
  canonical_name: string
  data_type: string
  description: string
  pii_classification: string
  semantic_category: string
  confidence_score: number
  mapping_decision: string
  existing_canonical_name: string | null
  similarity_reasoning: string
  deduplication_confidence: number
  source_mappings: Record<string, string>
  ai_generation_context: {
    confidence_score: number
    reasoning: string
    sample_data_analyzed: string[]
  }
  generated_at: string
  generated_by: string
  reviewed_by: string | null
  approved_at: string | null
  conflict_statistics: Record<string, any>
  conflict_rules_last_updated: string
}

export interface AnalyzeFileResponse {
  upload_id: number
  s3_path: string
  category: string
  suggested_mappings: SuggestedMapping[]
  stored_rules: Record<string, string>
  critical_field_error?: string
  missing_critical_fields?: string[]
  timestamp: string
}

export interface FieldMapping {
  id: number
  fieldName: string
  originalName: string
  dataType: string
  useOriginal: boolean
  mapping: string
  isPreferred: boolean
  confidenceScore?: number
}

export interface SaveFieldMappingsRequest {
  upload_id: number
  data_source_id: number
  field_mappings: Record<string, string>
}

export interface SaveFieldMappingsResponse {
  success: boolean
  message: string
  data: {
    upload_id: number
    data_source_id: number
    mappings_saved: number
    canonical_fields_saved: string[]
    field_mappings_detail: Record<string, string>
    timestamp: string
  }
}

export interface ValidationRule {
  field: string
  validation_instruction: string
}

export interface SaveValidationRulesRequest {
  upload_id: number
  data_source_id: number
  validation_rules: ValidationRule[]
}

export interface SaveValidationRulesResponse {
  success: boolean
  message: string
  data: {
    upload_id: number
    data_source_id: number
    rules_saved: number
    timestamp: string
  }
}

export interface GetFieldMappingsResponse {
  success: boolean
  message: string
  data: {
    data_source_id: number
    mappings_count: number
    field_mappings: Array<{
      original_field: string
      canonical_field: string
      data_type?: string
      confidence_score?: number
    }>
    timestamp: string
  }
}

export interface GetValidationRulesResponse {
  success: boolean
  message: string
  data: {
    upload_id: number
    data_source_id: number
    rules_count: number
    validation_rules: Array<{
      canonical_field: string
      validation_instruction: string
    }>
    timestamp: string
  }
}

export interface ResolutionStrategy {
  field: string
  strategy: string
}

export interface SaveResolutionStrategiesRequest {
  upload_id: number
  data_source_id: number
  resolution_strategies: ResolutionStrategy[]
}

export interface SaveResolutionStrategiesResponse {
  success: boolean
  message: string
  data: {
    upload_id: number
    data_source_id: number
    strategies_saved: number
    timestamp: string
  }
}

export interface GetResolutionStrategiesResponse {
  success: boolean
  message: string
  data: {
    upload_id: number
    data_source_id: number
    strategies_count: number
    resolution_strategies: Array<{
      canonical_field: string
      resolution_strategy: string
      strategy_config: {
        use_global_priority: boolean
      }
      created_at: string
      updated_at: string
    }>
    timestamp: string
  }
}

export interface TriggerProcessingRequest {
  upload_id: number
  data_source_id: number
  category: string
  processing_method: string
}

export interface TriggerProcessingResponse {
  success: boolean
  message: string
  data?: {
    processing_id: number
    status: string
    timestamp: string
  }
  error_code?: string
  details?: {
    missing_configs?: string[]
    critical_field_error?: string
  }
}

export interface DeleteValidationRuleResponse {
  success: boolean
  message: string
  data: {
    data_source_id: number
    canonical_field: string
    deleted: boolean
    message: string
    timestamp: string
  }
}

export interface DeleteResolutionStrategyResponse {
  success: boolean
  message: string
  data: {
    upload_id: number
    canonical_field: string
    deleted: boolean
    message: string
    timestamp: string
  }
}

class FileAnalysisService {
  /**
   * Analyze uploaded file to get field mappings and suggestions
   */
  async analyzeUploadedFile(
    request: AnalyzeFileRequest
  ): Promise<AnalyzeFileResponse> {
    try {
      // File analysis can take longer, so increase timeout to 5 minutes
      const response = await api.post<AnalyzeFileResponse>(
        '/data-pipeline/analyze-uploaded-file',
        request,
        {
          timeout: 300000, // 300 seconds (5 minutes) timeout for file analysis
        }
      )
      return response.data
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to analyze uploaded file')
      console.error(errorMessage)
      throw new Error(errorMessage)
    }
  }

  /**
   * Convert API response to field mappings for UI display
   */
  convertToFieldMappings(response: AnalyzeFileResponse): FieldMapping[] {
    return response.suggested_mappings.map((mapping, index) => ({
      id: index + 1,
      fieldName: mapping.canonical_name,
      originalName: mapping.original_header,
      dataType: mapping.data_type,
      useOriginal: false,
      mapping: mapping.original_header,
      isPreferred: mapping.confidence_score >= 90, // Consider high confidence as AI preferred
      confidenceScore: mapping.confidence_score / 100, // Convert to decimal if needed
    }))
  }

  /**
   * Save field mappings to the database
   */
  async saveFieldMappings(request: SaveFieldMappingsRequest): Promise<SaveFieldMappingsResponse> {
    try {
      const response = await api.post<SaveFieldMappingsResponse>(
        '/field-mappings/save-field-mappings',
        request,
        {
          timeout: 60000 // 60 seconds timeout for save operation
        }
      )
      return response.data
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to save field mappings')
      console.error(errorMessage)
      throw new Error(errorMessage)
    }
  }

  /**
   * Convert UI field mappings to save request format
   */
  convertToSaveRequest(
    fieldMappings: FieldMapping[],
    uploadId: number,
    dataSourceId: number
  ): SaveFieldMappingsRequest {
    const field_mappings: Record<string, string> = {}

    fieldMappings.forEach(mapping => {
      // Logic: If "Use Original" is checked, map to original name, otherwise use AI generated field name
      field_mappings[mapping.originalName] = mapping.useOriginal
        ? mapping.originalName
        : mapping.fieldName
    })

    return {
      upload_id: uploadId,
      data_source_id: dataSourceId,
      field_mappings
    }
  }

  /**
   * Save validation rules to the database
   */
  async saveValidationRules(request: SaveValidationRulesRequest): Promise<SaveValidationRulesResponse> {
    try {
      const response = await api.post<SaveValidationRulesResponse>(
        '/validation-rules/save-validation-rules',
        request,
        {
          timeout: 30000 // 30 seconds timeout for save operation
        }
      )
      return response.data
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to save validation rules')
      console.error(errorMessage)
      throw new Error(errorMessage)
    }
  }

  /**
   * Get existing field mappings from the database
   */
  async getFieldMappings(dataSourceId: number): Promise<GetFieldMappingsResponse> {
    try {
      const response = await api.get<GetFieldMappingsResponse>(
        `/field-mappings/get-field-mappings?data_source_id=${dataSourceId}`,
        {
          timeout: 30000 // 30 seconds timeout for get operation
        }
      )
      return response.data
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to get field mappings')
      console.error(errorMessage)
      throw new Error(errorMessage)
    }
  }

  /**
   * Get existing validation rules from the database
   */
  async getValidationRules(uploadId: number, dataSourceId: number): Promise<GetValidationRulesResponse> {
    try {
      const response = await api.get<GetValidationRulesResponse>(
        `/validation-rules/get-validation-rules?upload_id=${uploadId}&data_source_id=${dataSourceId}`,
        {
          timeout: 30000 // 30 seconds timeout for get operation
        }
      )
      return response.data
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to get validation rules')
      console.error(errorMessage)
      throw error
    }
  }

  /**
   * Save resolution strategies to the database
   */
  async saveResolutionStrategies(request: SaveResolutionStrategiesRequest): Promise<SaveResolutionStrategiesResponse> {
    try {
      const response = await api.post<SaveResolutionStrategiesResponse>(
        '/resolution-strategies/save-resolution-strategies',
        request,
        {
          timeout: 30000 // 30 seconds timeout for save operation
        }
      )
      return response.data
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to save resolution strategies')
      console.error(errorMessage)
      throw new Error(errorMessage)
    }
  }

  /**
   * Get existing resolution strategies from the database
   */
  async getResolutionStrategies(uploadId: number, dataSourceId: number): Promise<GetResolutionStrategiesResponse> {
    try {
      const response = await api.get<GetResolutionStrategiesResponse>(
        `/resolution-strategies/get-resolution-strategies?upload_id=${uploadId}&data_source_id=${dataSourceId}`,
        {
          timeout: 30000 // 30 seconds timeout for get operation
        }
      )
      return response.data
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to get resolution strategies')
      console.error(errorMessage)
      throw new Error(errorMessage)
    }
  }

  /**
   * Trigger data pipeline processing
   */
  async triggerProcessing(request: TriggerProcessingRequest): Promise<TriggerProcessingResponse> {
    try {
      const response = await api.post<TriggerProcessingResponse>(
        '/data-pipeline/trigger-processing',
        request,
        {
          timeout: 300000 // 300 seconds (5 minutes) timeout for processing trigger
        }
      )
      return response.data
    } catch (error: any) {
      console.error('Failed to trigger processing:', error)

      if (error?.response?.data) {
        const errorData = error.response.data
        throw {
          message: errorData.message || 'Failed to trigger processing',
          error_code: errorData.error_code,
          details: errorData.details,
          response: error.response
        }
      }

      throw error
    }
  }

  /**
   * Delete a validation rule for a specific field
   */
  async deleteValidationRule(dataSourceId: number, canonicalField: string): Promise<DeleteValidationRuleResponse> {
    try {
      const response = await api.delete<DeleteValidationRuleResponse>(
        `/validation-rules/delete-validation-rule?data_source_id=${dataSourceId}&canonical_field=${canonicalField}`,
        {
          timeout: 30000 // 30 seconds timeout for delete operation
        }
      )
      return response.data
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to delete validation rule')
      console.error(errorMessage)
      throw new Error(errorMessage)
    }
  }

  /**
   * Delete a resolution strategy for a specific field
   */
  async deleteResolutionStrategy(uploadId: number, canonicalField: string): Promise<DeleteResolutionStrategyResponse> {
    try {
      const response = await api.delete<DeleteResolutionStrategyResponse>(
        `/resolution-strategies/delete-resolution-strategy?upload_id=${uploadId}&canonical_field=${canonicalField}`,
        {
          timeout: 30000 // 30 seconds timeout for delete operation
        }
      )
      return response.data
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to delete resolution strategy')
      console.error(errorMessage)
      throw new Error(errorMessage)
    }
  }
}

export const fileAnalysisService = new FileAnalysisService()
export default fileAnalysisService
